import { COLORS } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import dynamic from 'next/dynamic';
import { FC, Fragment } from 'react';

import { AgencySearchBlock } from '_components/common/AgencySearchBlock';
import type { PressSectionProps } from '_components/common/PressSection';
import { SectionShape } from '_components/common/SectionShape';
import { StoryCard } from '_components/common/StoryCard';
import { YoutubeEmbed } from '_components/common/YoutubeEmbed';
import { PageSeoTag } from '_components/SEO';
import { usePublicAppContext } from '_core/context/public-app-context';
import { PublicApiRepo, useGetLinks, useGetPage } from '_core/repos/public-api.repo';
import { HeaderComponent } from '_features/story/components';
import { storyConfig } from '_features/story/story.config';
import { getRelatedLink, getStoryInfo } from '_features/story/utils';

const PressSection = dynamic<PressSectionProps>(
  () => import('_components/common/PressSection').then((mod) => mod.PressSection),
  {
    ssr: false,
  },
);

const StoryPage: FC = () => {
  const { t } = useTranslation(storyConfig.i18nNamespaces);
  const { data: pageData } = useGetPage(PublicApiRepo.publicApiUrl);
  const { canonical } = usePublicAppContext();
  const { data: relatedStories } = useGetLinks(PublicApiRepo.publicApiUrl);
  const story = getStoryInfo(pageData?.data.attributes, t);
  const attributes = pageData?.data.attributes;

  return (
    <Fragment>
      <PageSeoTag
        title={attributes?.metadata.title_tag ? attributes?.metadata.title_tag : 'Sortlist'}
        description={
          attributes?.metadata.metadescription ? attributes?.metadata.metadescription : t('story:seo.description')
        }
        image={attributes?.description.imageUrl}
        canonical={canonical}
      />

      <HeaderComponent story={story} />

      <SectionShape
        color={story?.business.type === 'client' ? COLORS.DEMAND_200 : COLORS.SUPPLY_200}
        height={80}
        className="hide-xs"
      />

      {story?.videoUrl && (
        <section className="container-md pt-16">
          <YoutubeEmbed url={story?.videoUrl} title={story?.business.name}></YoutubeEmbed>
        </section>
      )}

      <section className="pt-12 pb-16">
        <div className="container-md">
          {story?.content.descriptionTitle && <h2 className="h1 bold">{story?.content.descriptionTitle}</h2>}

          {story?.content.paragraphs.map((paragraph, key) => {
            return (
              <div key={key} className="section">
                {paragraph.title && <h4 className="bold mt-12 mb-4">{paragraph.title}</h4>}
                {paragraph.body && <p className="h6 lh-3 text-secondary-500">{paragraph.body}</p>}
              </div>
            );
          })}
        </div>
      </section>

      {relatedStories && relatedStories.data.length > 0 && (
        <section className="bg-secondary-50 py-24">
          <div className="container-lg">
            <h2 className="text-center h1 bold serif">
              <Trans i18nKey={'story:seeMore.title'} values={{ name: story?.business.name }}>
                You got moved by <span className="text-primary-500">{'{{name}}'}</span> Story?
              </Trans>
            </h2>
            <h3 className="text-center mb-8 mt-2 h6">{t('story:seeMore.subTitle')}</h3>
            <div className="layout-row layout-align-space-around-center layout-wrap">
              {relatedStories?.data.map((story, key) => {
                const relatedLink = getRelatedLink(story, relatedStories);
                return (
                  <div key={key} className="flex-100 flex-sm-50 flex-gt-sm-33 p-4 layout-column">
                    <StoryCard linkStories={story?.attributes} link={relatedLink} className="flex-1" />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
      <PressSection
        mode="dark"
        title={
          <Trans i18nKey={'story:press.title'}>
            People talk about us in the <b>press</b>
          </Trans>
        }
        data-testid="home-press-section"
      />
      <div className="container-lg py-16">
        <AgencySearchBlock data-testid="agency-search-block" trackingPage={'story'} />
      </div>
    </Fragment>
  );
};

export default StoryPage;
